import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  X,
  Loader2,
  AlertCircle,
  DollarSign,
  FileText,
  CheckCircle,
} from 'lucide-react';

/**
 * RefundRequestModal - Modal for requesting a refund on a payment
 *
 * Props:
 * - isOpen: boolean - Whether modal is open
 * - onClose: function - Called when modal is closed
 * - payment: object - The payment to refund
 * - invoice: object - The invoice associated with the payment
 * - onRefundRequested: function - Called after successful refund request
 */
const RefundRequestModal = ({ isOpen, onClose, payment, invoice, onRefundRequested }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Form state
  const [refundType, setRefundType] = useState('full'); // 'full' or 'partial'
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reasonNotes, setReasonNotes] = useState('');

  // Available refund reasons
  const refundReasons = [
    { value: 'duplicate_charge', label: 'Duplicate Charge' },
    { value: 'service_not_provided', label: 'Service Not Provided' },
    { value: 'billing_error', label: 'Billing Error' },
    { value: 'customer_request', label: 'Customer Request' },
    { value: 'subscription_cancellation', label: 'Subscription Cancellation' },
    { value: 'other', label: 'Other' },
  ];

  // Calculate available refund amount
  const availableForRefund = payment
    ? payment.amount - (payment.refunded_amount || 0)
    : 0;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRefundType('full');
      setAmount('');
      setReason('');
      setReasonNotes('');
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  const formatCurrency = (amountInCents, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amountInCents / 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!reason) {
      setError('Please select a refund reason');
      return;
    }

    if (reason === 'other' && !reasonNotes.trim()) {
      setError('Please provide details for "Other" reason');
      return;
    }

    let refundAmount;
    if (refundType === 'full') {
      refundAmount = availableForRefund;
    } else {
      refundAmount = Math.round(parseFloat(amount) * 100);
      if (isNaN(refundAmount) || refundAmount <= 0) {
        setError('Please enter a valid refund amount');
        return;
      }
      if (refundAmount > availableForRefund) {
        setError(`Refund amount cannot exceed ${formatCurrency(availableForRefund)}`);
        return;
      }
    }

    try {
      setLoading(true);

      await axios.post(
        `${getApiBase()}/billing/refunds`,
        {
          payment_id: payment.id,
          amount: refundAmount,
          reason: reason,
          reason_notes: reasonNotes || null,
        },
        { headers: getAuthHeaders() }
      );

      setSuccess(true);

      // Notify parent after short delay
      setTimeout(() => {
        if (onRefundRequested) {
          onRefundRequested();
        }
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Error requesting refund:', err);
      setError(err.response?.data?.detail || 'Failed to submit refund request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Success state
  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Refund Request Submitted
          </h3>
          <p className="text-gray-600">
            Your refund request has been submitted and is pending approval.
            {availableForRefund >= 10000 && (
              <span className="block mt-2 text-sm text-gray-500">
                Note: Refunds of $100 or more require dual approval.
              </span>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Request Refund</h3>
            {invoice && (
              <p className="text-sm text-gray-500">
                Invoice #{invoice.invoice_number}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Payment Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Original Payment</span>
              <span className="font-medium">
                {formatCurrency(payment?.amount, payment?.currency)}
              </span>
            </div>
            {(payment?.refunded_amount || 0) > 0 && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">Already Refunded</span>
                <span className="text-red-600">
                  -{formatCurrency(payment.refunded_amount, payment?.currency)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t">
              <span className="text-sm font-medium">Available for Refund</span>
              <span className="font-semibold text-green-600">
                {formatCurrency(availableForRefund, payment?.currency)}
              </span>
            </div>
          </div>

          {/* Refund Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Refund Amount
            </label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="refundType"
                  value="full"
                  checked={refundType === 'full'}
                  onChange={(e) => setRefundType(e.target.value)}
                  className="mr-3"
                />
                <div>
                  <span className="font-medium">Full Refund</span>
                  <span className="ml-2 text-gray-600">
                    ({formatCurrency(availableForRefund, payment?.currency)})
                  </span>
                </div>
              </label>

              <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="refundType"
                  value="partial"
                  checked={refundType === 'partial'}
                  onChange={(e) => setRefundType(e.target.value)}
                  className="mr-3"
                />
                <span className="font-medium">Partial Refund</span>
              </label>

              {refundType === 'partial' && (
                <div className="ml-6 mt-2">
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={(availableForRefund / 100).toFixed(2)}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum: {formatCurrency(availableForRefund, payment?.currency)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Refund Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Refund <span className="text-red-500">*</span>
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select a reason...</option>
              {refundReasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
              {reason === 'other' && <span className="text-red-500"> *</span>}
            </label>
            <textarea
              value={reasonNotes}
              onChange={(e) => setReasonNotes(e.target.value)}
              placeholder="Provide additional details about this refund request..."
              rows={3}
              className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              required={reason === 'other'}
            />
          </div>

          {/* Dual Approval Notice */}
          {(refundType === 'full' ? availableForRefund : (parseFloat(amount) || 0) * 100) >= 10000 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Dual Approval Required
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    Refunds of $100 or more require approval from two authorized users
                    before processing.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !reason}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Refund Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RefundRequestModal;
